import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Catalog, activeCatalogSelector, availableCatalogs, currentCatalog } from '@vi-succ/products-domain';
import { NGXLogger } from 'ngx-logger';
import { Observable, catchError, filter, firstValueFrom, map, mapTo, of, retry, switchMap } from 'rxjs';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';
import { concatMap } from 'rxjs/operators';
import { ContextInfo } from '../core/login/models/login.model';
import { environment } from '../environments/environment';
import { countryToSalesOrg, findCatalogPreselection } from '../util';
import { StorageService } from './storage.service';

interface PersistedCatalogs {
  catalogs: Catalog[];
  timestamp: string;
}

@Injectable({
  providedIn: 'root',
})
export class EtCatalogDataService {
  private static readonly IGNORED_SALESORGS = ['1976', '2700'];

  constructor(
    private http: HttpClient,
    private storageService: StorageService,
    private translate: TranslateService,
    private logger: NGXLogger,
    private store: Store
  ) {}

  setActiveCatalog(catalog: Catalog) {
    this.store.dispatch(currentCatalog(catalog));
    return this.storageService.set('currentCatalog', catalog);
  }

  setAvailableCatalogs(catalogs: Catalog[]) {
    this.store.dispatch(availableCatalogs(catalogs));
  }

  async validateUserCatalogs(contextInfo: ContextInfo): Promise<Catalog | undefined> {
    let activeCatalog = await firstValueFrom(this.getLocalCatalog());
    const userSalesOrgs = contextInfo.salesOrgs ? contextInfo.salesOrgs.map((s) => s.salesOrg) : [];

    let userCatalogs: Catalog[] = [];
    const catalogs = await firstValueFrom(this.getCatalogs());

    userCatalogs = catalogs.filter((c) => !contextInfo.customer || userSalesOrgs.includes(c.salesOrg));
    this.setAvailableCatalogs(userCatalogs);

    if (contextInfo.customer && !userSalesOrgs.includes(activeCatalog.salesOrg)) {
      const language = contextInfo.language ?? this.translate.currentLang.substring(0, 2);
      const salesOrg = contextInfo.activeSalesOrg
        ? contextInfo.activeSalesOrg.salesOrg
        : countryToSalesOrg(language.toUpperCase());

      const foundCatalog = userCatalogs.find(
        (catalog) => catalog.salesOrg === salesOrg && catalog.language === language
      );

      if (foundCatalog) {
        activeCatalog = foundCatalog;
      } else if (userCatalogs.length > 0) {
        activeCatalog = userCatalogs[0];
      }

      await this.setActiveCatalog(activeCatalog);
      return activeCatalog;
    } else {
      return undefined;
    }
  }

  getCurrentCatalog(): Observable<Catalog> {
    return this.getLocalCatalog();
  }

  getCatalogs(): Observable<Catalog[]> {
    return this._getLocalCatalog().pipe(
      filter((value) => !!value),
      map((catalogs) => catalogs.filter((c) => !EtCatalogDataService.IGNORED_SALESORGS.includes(c.salesOrg)))
    );
  }

  private _getLocalCatalog(): Observable<Catalog[]> {
    // Read from storage if available and not older than 7 days
    return fromPromise(this.storageService.get<PersistedCatalogs>('catalogs')).pipe(
      switchMap((localCatalogs) => {
        if (localCatalogs) {
          this.logger.debug('Found catalogs in storage');
          const localCatalogsObj = localCatalogs;
          if (new Date(localCatalogsObj.timestamp) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)) {
            return of(localCatalogsObj.catalogs);
          }
          this.logger.debug('Catalogs in storage are older than 7 days');
        }

        return this.http.get<Catalog[]>(`${environment.backend.url}/catalogs`).pipe(
          retry(2),
          switchMap((catalogs) => {
            // Write catalogs to local storage with timestamp
            return fromPromise(
              this.storageService.set<PersistedCatalogs>('catalogs', {
                catalogs: catalogs,
                timestamp: new Date().toISOString(),
              })
            ).pipe(mapTo(catalogs));
          }),
          catchError((err: HttpErrorResponse) => {
            this.logger.error('Error while fetching catalogs', err);
            // If error, try to get catalogs from local storage regardless of timestamp
            return fromPromise(this.storageService.get<PersistedCatalogs>('catalogs')).pipe(
              switchMap((localCatalogs) => {
                if (localCatalogs) {
                  return of(localCatalogs?.catalogs ?? []);
                }
                return of([]);
              })
            );
          })
        );
      })
    );
  }

  private getLocalCatalog(): Observable<Catalog> {
    return this.store.select(activeCatalogSelector).pipe(
      concatMap((catalog) => {
        if (catalog) return of(catalog);
        else
          return fromPromise(this.storageService.get<Catalog>('currentCatalog')).pipe(
            concatMap((catalog) => {
              if (catalog) {
                this.store.dispatch(currentCatalog(catalog));
                return this.store.select(activeCatalogSelector);
              }

              return this.getCatalogs().pipe(
                concatMap((catalogs) => fromPromise(findCatalogPreselection(catalogs))),
                concatMap((catalog) => {
                  this.store.dispatch(currentCatalog(catalog));
                  return fromPromise(this.storageService.set('currentCatalog', catalog)).pipe(
                    switchMap(() => this.store.select(activeCatalogSelector))
                  );
                })
              );
            })
          );
      }),
      filter((catalog): catalog is Catalog => !!catalog)
    );
  }
}
